import {
  Locale,
  SignalNode,
  ProofItem,
  SelectedWorkChapter,
  IncidentStep,
  ExperienceItem,
  CapabilityItem,
  CaseStudy,
  PrincipleItem,
  ToolStackGroup,
} from '../types';

export interface SiteContent {
  meta: {
    title: string;
    description: string;
  };
  header: {
    brand: string;
    mode30s: string;
    modeFull: string;
    nav: {
      flow: string;
      scale: string;
      work: string;
      incident: string;
      capabilities: string;
      experience: string;
      cases: string;
      principles: string;
      stack: string;
      contact: string;
    };
  };
  hero: {
    eyebrow: string;
    name: string;
    headlineLead: string;
    headlineEmph: string;
    title: string;
    summary: string;
    viewExpButton: string;
    contactButton: string;
    availability: string;
    flowLabel: string;
    flowCounter: string;
    signalPrompt: string;
    systemResponsePrompt: string;
  };
  signalNodes: SignalNode[];
  proofScale: {
    eyebrow: string;
    title: string;
    items: ProofItem[];
    extendedItems: ProofItem[];
  };
  selectedWork: {
    eyebrow: string;
    title: string;
    subtitle: string;
    chapters: SelectedWorkChapter[];
  };
  pushIncident: {
    eyebrow: string;
    title: string;
    subtitle: string;
    badge: string;
    caseName: string;
    steps: IncidentStep[];
    navigation: {
      prev: string;
      next: string;
      stepOf: (current: number, total: number) => string;
    };
    sourceNotice: string;
  };
  fullExperience: {
    eyebrow: string;
    title: string;
    dateDisclaimer: string;
    items: ExperienceItem[];
  };
  capabilities: {
    eyebrow: string;
    title: string;
    titleEmphasis: string;
    items: CapabilityItem[];
  };
  impactSection: {
    eyebrow: string;
    title: string;
    aside: string;
    items: { stat: string; label: string }[];
  };
  casesSection: {
    eyebrow: string;
    title: string;
    titleEmphasis: string;
    labels: {
      problem: string;
      constraint: string;
      action: string;
      result: string;
    };
    items: CaseStudy[];
  };
  principles: {
    eyebrow: string;
    title: string;
    titleEmphasis: string;
    aside: string;
    items: PrincipleItem[];
  };
  toolStack: {
    eyebrow: string;
    title: string;
    titleEmphasis: string;
    groups: ToolStackGroup[];
  };
  aboutContext: {
    eyebrow: string;
    title: string;
    titleEmphasis: string;
    paragraphs: string[];
    tags: string[];
  };
  contact: {
    eyebrow: string;
    title: string;
    description: string;
    email: string;
  };
  footer: {
    brandTag: string;
    backToTop: string;
  };
  modeBanner: {
    title30s: string;
    desc30s: string;
    switchPrompt: string;
    switchToFull: string;
    switchTo30s: string;
  };
}

export const content: Record<Locale, SiteContent> = {
  en: {
    meta: {
      title: 'Dmitry Yakovlev — Technical Support & Service Operations Leader',
      description: 'Technical support leader connecting customer signal, support operations, engineering and product.',
    },
    header: {
      brand: 'DY/',
      mode30s: '30 SEC',
      modeFull: 'FULL CV',
      nav: {
        flow: 'Signal Path',
        scale: 'Scale',
        work: 'Selected Work',
        incident: 'Push Incident',
        capabilities: 'What I build',
        experience: 'Experience',
        cases: 'Cases',
        principles: 'Principles',
        stack: 'Tools',
        contact: 'Contact',
      },
    },
    hero: {
      eyebrow: 'DMITRY YAKOVLEV · SUPPORT SYSTEMS',
      name: 'DMITRY YAKOVLEV',
      headlineLead: 'customer support is a system.',
      headlineEmph: 'not a queue.',
      title: 'Customer Technical Support Leader',
      summary:
        'I connect customer service, technical diagnosis and operating discipline so problems reach the right owner, customers understand what is happening, and teams remove causes rather than symptoms.',
      viewExpButton: 'View experience',
      contactButton: 'Email Dmitry',
      availability: 'Support systems · Service operations · Incident & Problem Management',
      flowLabel: 'Support system pipeline',
      flowCounter: '01 / 05',
      signalPrompt: 'Customer signal',
      systemResponsePrompt: 'System response',
    },
    signalNodes: [
      {
        id: 'signal',
        index: '01',
        title: 'CUSTOMER SIGNAL',
        tag: 'INBOUND TELEMETRY',
        detail:
          'Customer reports a failure or telemetry detects an anomaly (REST API, SMPP, SMS, Push, Viber, Telegram, Webhooks).',
      },
      {
        id: 'support',
        index: '02',
        title: 'SUPPORT',
        tag: 'TRIAGE & DIAGNOSIS',
        detail:
          'L1/L2/L3 triage, validation of delivery statuses, TTL, payloads, and blast radius isolation.',
      },
      {
        id: 'owner',
        index: '03',
        title: 'OWNER',
        tag: 'EXPLICIT OWNERSHIP',
        detail:
          'Designated incident owner assigned across engineering, product and operations without handoff ping-pong.',
      },
      {
        id: 'fix',
        index: '04',
        title: 'FIX',
        tag: 'REMEDIATION',
        detail:
          'Remediation of platform or configuration defects at the source rather than masking symptoms.',
      },
      {
        id: 'system-change',
        index: '05',
        title: 'SYSTEM CHANGE',
        tag: 'PREVENTION & OBSERVABILITY',
        detail:
          'End-to-end observability, updated runbooks, Known Issues documentation, and SLA/OLA governance.',
      },
    ],
    proofScale: {
      eyebrow: 'Scale and impact',
      title: 'Operating scale verified in the source.',
      items: [
        {
          id: 'microservices',
          index: '01',
          stat: '250+',
          label: 'microservices in an L3 support scope',
          context: 'Samokat / Smart Space LLC — Customer & commercial domains',
        },
        {
          id: 'services',
          index: '02',
          stat: '15',
          label: 'high-load services under management',
          context: 'Postal Technologies — 800,000+ total active audience',
        },
        {
          id: 'products',
          index: '03',
          stat: '10+',
          label: 'products transitioned into support',
          context: 'Samokat / Smart Space LLC — Operational readiness & Service Transition',
        },
      ],
      extendedItems: [
        {
          id: 'scaling',
          index: '04',
          stat: '3×',
          label: 'team scale-up',
          context: 'Samokat / Smart Space LLC — Scaled L3 team & promoted 5 specialists to BA',
        },
        {
          id: 'interviews',
          index: '05',
          stat: '150+',
          label: 'interviews conducted personally',
          context: 'Samokat / Smart Space LLC — Technical hiring & talent evaluation',
        },
      ],
    },
    selectedWork: {
      eyebrow: 'Selected Work',
      title: 'Four operational chapters.',
      subtitle: 'Structured situation and outcome chapters representing core operating phases.',
      chapters: [
        {
          id: 'build-kdl',
          chapter: 'CHAPTER 01 / BUILD',
          company: 'KDL Clinical Diagnostic Laboratories',
          role: 'Head of Technical Support',
          summary: 'Built a Service Desk and technical support operating model from the ground up.',
          bullets: [
            'ITSM processes, SLA, hiring, team development, automation, budget and vendors.',
            'Discovery, launch of new support directions, technical specifications, regulations and operational documentation.',
            'Jira, Confluence and monitoring systems for tasks, knowledge and support operations.',
          ],
        },
        {
          id: 'scale-samokat',
          chapter: 'CHAPTER 02 / SCALE',
          company: 'Samokat / Smart Space LLC',
          role: 'Head of L3 Product Support',
          summary:
            'L3 support for Samokat and Megamarket product platforms: more than 250 microservices across customer and commercial domains.',
          bullets: [
            'Introduced ITIL practices, SLA, more than 20 operational metrics and monitoring elements.',
            'Transitioned more than 10 products into support.',
            'Scaled the team threefold; conducted more than 150 interviews; helped 5 specialists move into business analysis roles.',
          ],
        },
        {
          id: 'operate-postal',
          chapter: 'CHAPTER 03 / OPERATE',
          company: 'Postal Technologies',
          role: 'Head of Information Technology',
          summary: 'IT support for 15 high-load services with a combined audience of more than 800,000 users.',
          bullets: [
            'Incident Management, SLA, cross-functional operations and management reporting.',
            'Reduced incident resolution time by 25% through process automation and stronger technical coordination.',
            'Reduced technical support costs by 20% through regular business work on SLA and service quality.',
          ],
        },
        {
          id: 'incident-rapporto',
          chapter: 'CHAPTER 04 / INCIDENT / CPaaS',
          company: 'Rapporto',
          role: 'Head of Customer Technical Support',
          summary:
            'Customer service stability, SLA and distributed technical support for a B2B CPaaS platform.',
          bullets: [
            'Coordinated L1/L2/L3, development, operations, product, account managers, carriers and external partners.',
            'REST API, SMPP, SMS, Push, Viber and Telegram; routing, TTL, delivery statuses, latency and service failures.',
            'Audited OTRS, developed Service Desk processes, observability, runbooks, Known Issues and operational documentation.',
          ],
        },
      ],
    },
    pushIncident: {
      eyebrow: 'Real Incident Flow',
      title: 'Push incident for a major bank.',
      subtitle: 'From incoming customer alert to durable systemic change.',
      badge: 'CASE 02 / INCIDENT MANAGEMENT',
      caseName: 'Push incident for a major bank',
      steps: [
        {
          id: 'signal',
          step: '01',
          phase: 'SIGNAL',
          title: 'Customer-Reported Failure',
          context: 'Customer reported the issue first',
          detail:
            'The client (a major bank) was the first to notify that push notifications were failing in production.',
          systemAction:
            'Inbound signal captured from customer before internal platform alarms surfaced client-specific disruption.',
        },
        {
          id: 'triage',
          step: '02',
          phase: 'TRIAGE',
          title: 'Divergent Platform Behavior',
          context: 'Multi-platform triage',
          detail:
            'Initial technical diagnostics revealed two distinct failure patterns: iOS push was not handled by Notification Service; Android path depended on client configuration or tokens.',
          systemAction:
            'L1/L2 diagnostics isolated mobile OS failure modes to prevent confounding iOS and Android issues.',
        },
        {
          id: 'split',
          step: '03',
          phase: 'SPLIT HYPOTHESIS',
          title: 'Decoupling Platform vs. Client Configuration',
          context: 'No convenient end-to-end tracing',
          detail:
            'Because there was no convenient end-to-end trace and no existing clear ownership, Dmitry split the investigation into two isolated tracks: platform-side cause vs. customer configuration.',
          systemAction:
            'Prevented premature conclusions and finger-pointing by isolating server-side CPaaS deliverability from device-level token synchronization.',
        },
        {
          id: 'ownership',
          step: '04',
          phase: 'OWNERSHIP',
          title: 'Cross-Functional Incident Command',
          context: 'Clear ownership restored',
          detail:
            'Dmitry collected technical context, coordinated L1/L2/L3, development, product, and account managers, and organized structured customer communication.',
          systemAction:
            'Established single incident command to direct engineering investigation while giving account managers verified technical updates for the bank.',
        },
        {
          id: 'followup',
          step: '05',
          phase: 'FOLLOW-UP',
          title: 'Systemic Resolution',
          context: 'From single failure to system actions',
          detail:
            'The single failure was turned into systemic actions: formulated end-to-end observability requirements, documented clear ownership boundaries, and updated runbooks.',
          systemAction:
            'Resolved root failure and established observability, operational documentation, and Known Issues to prevent recurrence.',
        },
      ],
      navigation: {
        prev: '← PREV STAGE',
        next: 'NEXT STAGE →',
        stepOf: (current, total) => `STAGE ${current} OF ${total}`,
      },
      sourceNotice: 'All stages and technical boundaries are taken directly from the verified source incident.',
    },
    fullExperience: {
      eyebrow: 'Experience',
      title: 'Operating across the whole path.',
      dateDisclaimer: 'Dates are not published in the source profile',
      items: [
        {
          id: 'rapporto',
          marker: '01',
          company: 'Rapporto',
          role: 'Head of Customer Technical Support',
          summary:
            'Customer service stability, SLA and distributed technical support for a B2B CPaaS platform.',
          bullets: [
            'Coordinated L1/L2/L3, development, operations, product, account managers, carriers and external partners.',
            'REST API, SMPP, SMS, Push, Viber and Telegram; routing, TTL, delivery statuses, latency and service failures.',
            'Audited OTRS, developed Service Desk processes, observability, runbooks, Known Issues and operational documentation.',
          ],
        },
        {
          id: 'samokat',
          marker: '02',
          company: 'Samokat / Smart Space LLC',
          role: 'Head of L3 Product Support',
          summary:
            'L3 support for Samokat and Megamarket product platforms: more than 250 microservices across customer and commercial domains.',
          bullets: [
            'Introduced ITIL practices, SLA, more than 20 operational metrics and monitoring elements.',
            'Transitioned more than 10 products into support.',
            'Scaled the team threefold; conducted more than 150 interviews; helped 5 specialists move into business analysis roles.',
          ],
        },
        {
          id: 'postal',
          marker: '03',
          company: 'Postal Technologies',
          role: 'Head of Information Technology',
          summary:
            'IT support for 15 high-load services with a combined audience of more than 800,000 users.',
          bullets: [
            'Incident Management, SLA, cross-functional operations and management reporting.',
            'Reduced incident resolution time by 25% through process automation and stronger technical coordination.',
            'Reduced technical support costs by 20% through regular business work on SLA and service quality.',
          ],
        },
        {
          id: 'sber',
          marker: '04',
          company: 'Sber',
          role: 'Support Team Lead',
          summary: 'Support for 50 key services used by more than 2 million active users.',
          bullets: [
            'Incident, Problem, Risk and Service Management; coordinated development, DevOps and support.',
            'Introduced KPI monitoring; stated SLA level was 99.5%.',
            'Reduced critical incident resolution time by 20% through better coordination.',
          ],
        },
        {
          id: 'kdl',
          marker: '05',
          company: 'KDL Clinical Diagnostic Laboratories',
          role: 'Head of Technical Support',
          summary: 'Built a Service Desk and technical support operating model from the ground up.',
          bullets: [
            'ITSM processes, SLA, hiring, team development, automation, budget and vendors.',
            'Discovery, launch of new support directions, technical specifications, regulations and operational documentation.',
            'Jira, Confluence and monitoring systems for tasks, knowledge and support operations.',
          ],
        },
        {
          id: 'energon',
          marker: '06',
          company: 'Energon',
          role: 'Helpdesk Team Lead',
          summary: 'Built a technical support service from scratch.',
        },
        {
          id: 'mosenergo',
          marker: '07',
          company: 'MOSENERGO',
          role: 'Helpdesk Team Lead',
          summary: 'Led a team of 7 Helpdesk / Call Center specialists.',
          bullets: [
            'Supported more than 200 users; handled escalations, training, reporting, budget and IT infrastructure.',
          ],
        },
        {
          id: 'mts',
          marker: '08',
          company: 'MTS',
          role: 'IT Technical Support Specialist L2 / Tier 2',
          summary:
            'Supported mobile communications and mobile internet services; handled diagnosis, reporting and training.',
        },
        {
          id: 'rosneft',
          marker: '09',
          company: 'Rosneft',
          role: 'Project Administration Lead / Middle Help Desk Engineer L1',
          summary:
            'IT projects, procurement, reporting, project documentation, first-line diagnosis and L1/L2/L3 escalation.',
        },
      ],
    },
    capabilities: {
      eyebrow: 'What I build',
      title: 'Support is a system,',
      titleEmphasis: 'not a queue.',
      items: [
        {
          id: 'cap-1',
          index: '01',
          title: 'Support systems',
          description:
            'L1–L3, one customer history, queues, SLA/OLA, ownership and clear escalation paths.',
        },
        {
          id: 'cap-2',
          index: '02',
          title: 'Incident operations',
          description:
            'Critical incident coordination: impact, timeline, communication, RCA and follow-up.',
        },
        {
          id: 'cap-3',
          index: '03',
          title: 'Service transition',
          description:
            'Preparing new products for support: capacity, documentation, metrics and operational readiness.',
        },
        {
          id: 'cap-4',
          index: '04',
          title: 'AI-first workflows',
          description:
            'Automating repetitive work and documenting processes for people, monitoring systems and AI agents.',
        },
      ],
    },
    impactSection: {
      eyebrow: 'Scale and impact',
      title: 'Scale, made visible.',
      aside:
        'I connect customer service, technical diagnosis and operating discipline so problems reach the right owner, customers understand what is happening, and teams remove causes rather than symptoms.',
      items: [
        { stat: '250+', label: 'microservices in an L3 support scope' },
        { stat: '15', label: 'high-load services under management' },
        { stat: '10+', label: 'products transitioned into support' },
        { stat: '3×', label: 'team scale-up' },
        { stat: '150+', label: 'interviews conducted personally' },
      ],
    },
    casesSection: {
      eyebrow: 'Working situations',
      title: 'From signal',
      titleEmphasis: 'to system change.',
      labels: {
        problem: 'Problem',
        constraint: 'Constraint',
        action: 'Action',
        result: 'Result',
      },
      items: [
        {
          id: 'case-1',
          tag: 'CASE 01 / SERVICE DESIGN',
          title: 'Service Desk audit and evolution',
          problem:
            'OTRS had accumulated queues, statuses and manual routing. Parts of the process lived in informal agreements and private messages.',
          constraint:
            'The goal was not to move chaos into a nicer interface. Ownership, SLA, roles, channels and exceptions had to be understood first.',
          action:
            'Systematized the current operating model, mapped queues and owners, and formed requirements for the target model, escalation rules and one customer history.',
          result:
            'Created a basis for comparing solutions and planning migration without losing customer context.',
        },
        {
          id: 'case-2',
          tag: 'CASE 02 / INCIDENT MANAGEMENT',
          title: 'Push incident for a major bank',
          problem:
            'The customer reported the issue first: iOS push was not handled by the Notification Service; the Android path depended on client configuration or tokens.',
          constraint:
            'There was no convenient end-to-end trace or clear ownership. The investigation had to separate platform cause from customer configuration without premature conclusions.',
          action:
            'Collected technical context, coordinated L1/L2/L3, development, product and account managers, and organized communication and follow-up.',
          result:
            'Turned a single failure into system actions around observability, ownership and documentation.',
        },
        {
          id: 'case-3',
          tag: 'CASE 03 / OBSERVABILITY',
          title: 'Latency in a logistics customer journey',
          problem:
            'The customer reported slowdowns. In some 30-second intervals, more than 90% of requests took longer than 3 seconds.',
          constraint:
            'The customer mainly used notifications and polled the API only for missing statuses. Average API response time did not describe real impact.',
          action:
            'Mapped the customer journey to timings and metrics, prepared the engineering and operations escalation, and specified customer-focused observability requirements.',
          result:
            'Moved the investigation from one slow operation to interval-wide degradation and customer impact.',
        },
      ],
    },
    principles: {
      eyebrow: 'How I work',
      title: 'Less noise.',
      titleEmphasis: 'More signal.',
      aside:
        'I see support as a function that keeps services stable while helping product and engineering teams find systemic problems.',
      items: [
        {
          id: 'remove',
          index: '01',
          keyword: 'REMOVE',
          title: 'Remove unnecessary handoffs',
          description:
            'One customer history matters more than internal department boundaries. Customers should not repeat the same problem to multiple teams.',
        },
        {
          id: 'simplify',
          index: '02',
          keyword: 'SIMPLIFY',
          title: 'Make ownership explicit',
          description:
            'Every incident needs a clear owner for communication, diagnosis and correction.',
        },
        {
          id: 'measure',
          index: '03',
          keyword: 'MEASURE',
          title: 'Measure impact',
          description:
            'A green dashboard does not equal a healthy customer journey. Watch latency, queues, statuses, retries and scenario degradation.',
        },
        {
          id: 'automate',
          index: '04',
          keyword: 'AUTOMATE',
          title: 'Automate the repeatable',
          description:
            'Turn routine into a metric, runbook, automation or a clear product and engineering action.',
        },
      ],
    },
    toolStack: {
      eyebrow: 'Tools',
      title: 'The working',
      titleEmphasis: 'layer.',
      groups: [
        {
          id: 'service-mgmt',
          title: 'Service management',
          chips: ['OTRS', 'Jira', 'Yandex Tracker', 'ITIL / ITSM', 'SLA / OLA'],
        },
        {
          id: 'monitoring-logs',
          title: 'Monitoring / logs',
          chips: ['Grafana', 'Grafana OnCall', 'Zabbix', 'Loki', 'ELK', 'Graylog'],
        },
        {
          id: 'integration',
          title: 'Integration domain',
          chips: ['REST API', 'SMPP', 'SMS', 'Push', 'Viber', 'Telegram', 'Webhooks', 'TTL'],
        },
        {
          id: 'working-layer',
          title: 'Working layer',
          chips: ['Linux', 'Docker', 'SQL', 'Git', 'Runbooks', 'Knowledge Management', 'AI tools'],
        },
      ],
    },
    aboutContext: {
      eyebrow: 'Context',
      title: 'A support leader',
      titleEmphasis: 'with technical depth.',
      paragraphs: [
        'Technical support and service operations leader with more than 13 years of experience across fintech, e-commerce, telecom and enterprise.',
        'I focus on building technical support organizations, Incident & Problem Management, transitioning new products into support, observability and service operations.',
        'I see support as a function that keeps services stable while helping product and engineering teams find systemic problems.',
      ],
      tags: [
        'Customer Support',
        'Service Operations',
        'Incident Management',
        'Customer Success',
        'Support Engineering',
        'AI-first workflows',
      ],
    },
    contact: {
      eyebrow: 'Contact',
      title: 'Have a difficult support system?',
      description:
        'Let’s discuss where customer signal gets lost, how to restore ownership and which changes will make the operation more controllable.',
      email: 'dmitry@yakovlefff.ru',
    },
    footer: {
      brandTag: 'Dmitry Yakovlev / Support Systems',
      backToTop: 'Home ↗',
    },
    modeBanner: {
      title30s: '30 SEC VIEW ACTIVE',
      desc30s: 'Displaying executive summary: signal topology, scale proof, selected work and push incident post-mortem.',
      switchPrompt: 'Looking for chronological employment, ITSM principles, and tool stack?',
      switchToFull: 'Switch to Full CV (9 companies & tools) →',
      switchTo30s: '← Return to 30 SEC executive mode',
    },
  },
  ru: {
    meta: {
      title: 'Дмитрий Яковлев — руководитель технической поддержки и сервисных операций',
      description: 'Руководитель технической поддержки, который соединяет клиентский сигнал, поддержку, инженерные и продуктовые команды.',
    },
    header: {
      brand: 'DY/',
      mode30s: '30 СЕК',
      modeFull: 'ПОЛНЫЙ CV',
      nav: {
        flow: 'Сигнал',
        scale: 'Масштаб',
        work: 'Кейсы опыта',
        incident: 'Push-инцидент',
        capabilities: 'Что строю',
        experience: 'Опыт',
        cases: 'Кейсы',
        principles: 'Принципы',
        stack: 'Стек',
        contact: 'Контакты',
      },
    },
    hero: {
      eyebrow: 'DMITRY YAKOVLEV · SUPPORT SYSTEMS',
      name: 'DMITRY YAKOVLEV',
      headlineLead: 'клиентская поддержка — система.',
      headlineEmph: 'не очередь.',
      title: 'Руководитель клиентской технической поддержки',
      summary:
        'Соединяю клиентский сервис, инженерную диагностику и операционное управление — чтобы проблема доходила до владельца, клиент понимал происходящее, а команда устраняла причину, а не только симптом.',
      viewExpButton: 'Посмотреть опыт',
      contactButton: 'Написать Дмитрию',
      availability: 'Support systems · Service operations · Incident & Problem Management',
      flowLabel: 'Пайплайн системы поддержки',
      flowCounter: '01 / 05',
      signalPrompt: 'Клиентский сигнал',
      systemResponsePrompt: 'Ответ системы',
    },
    signalNodes: [
      {
        id: 'signal',
        index: '01',
        title: 'КЛИЕНТСКИЙ СИГНАЛ',
        tag: 'ВХОДЯЩИЙ СИГНАЛ',
        detail:
          'Входящий сигнал о сбое от клиента или телеметрии (REST API, SMPP, Push, Viber, Telegram, Webhooks).',
      },
      {
        id: 'support',
        index: '02',
        title: 'ПОДДЕРЖКА',
        tag: 'ДИАГНОСТИКА И TRIAGE',
        detail:
          'L1/L2/L3 диагностика, проверка статусов доставки, TTL, полезной нагрузки и локализация зоны влияния.',
      },
      {
        id: 'owner',
        index: '03',
        title: 'ВЛАДЕЛЕЦ',
        tag: 'ЯВНЫЙ OWNERSHIP',
        detail:
          'Назначение единого владельца инцидента: координация инженерии, продукта и клиентской связи без потери контекста.',
      },
      {
        id: 'fix',
        index: '04',
        title: 'ИСПРАВЛЕНИЕ',
        tag: 'УСТРАНЕНИЕ ПРИЧИНЫ',
        detail:
          'Устранение дефекта на уровне платформы или конфигурации вместо временной маскировки симптома.',
      },
      {
        id: 'system-change',
        index: '05',
        title: 'ИЗМЕНЕНИЕ СИСТЕМЫ',
        tag: 'ПРЕДОТВРАЩЕНИЕ',
        detail:
          'Сквозная observability, обновление runbooks, документация Known Issues и развитие процессов SLA и Service Transition.',
      },
    ],
    proofScale: {
      eyebrow: 'Масштаб и влияние',
      title: 'Масштаб, подтверждённый в исходных данных.',
      items: [
        {
          id: 'microservices',
          index: '01',
          stat: '250+',
          label: 'микросервисов в контуре L3-поддержки',
          context: 'Самокат / ООО «Умное пространство» — клиентский и коммерческий блоки',
        },
        {
          id: 'services',
          index: '02',
          stat: '15',
          label: 'высоконагруженных сервисов под управлением',
          context: 'Почтовые технологии — общая аудитория более 800 000 пользователей',
        },
        {
          id: 'products',
          index: '03',
          stat: '10+',
          label: 'продуктов приняты на сопровождение',
          context: 'Самокат / ООО «Умное пространство» — Service Transition и готовность к эксплуатации',
        },
      ],
      extendedItems: [
        {
          id: 'scaling',
          index: '04',
          stat: '3×',
          label: 'масштабирование команды',
          context: 'Самокат / ООО «Умное пространство» — рост L3-команды; 5 специалистов выросли в BA',
        },
        {
          id: 'interviews',
          index: '05',
          stat: '150+',
          label: 'интервью проведено лично',
          context: 'Самокат / ООО «Умное пространство» — технический найм и оценка кандидатов',
        },
      ],
    },
    selectedWork: {
      eyebrow: 'Кейсы опыта',
      title: 'Четыре главы практики.',
      subtitle: 'Структурированные этапы управления: от запуска Service Desk до высоконагруженных B2B-платформ.',
      chapters: [
        {
          id: 'build-kdl',
          chapter: 'ГЛАВА 01 / BUILD',
          company: 'KDL Клинико-диагностические лаборатории',
          role: 'Руководитель отдела технической поддержки',
          summary: 'Построение Service Desk и операционной модели технической поддержки с нуля.',
          bullets: [
            'ITSM-процессы, SLA, найм, развитие команды, автоматизация, бюджет и подрядчики.',
            'Предпроектное исследование, запуск новых направлений, технические задания, регламенты и эксплуатационная документация.',
            'Jira, Confluence и системы мониторинга для задач, знаний и процессов сопровождения.',
          ],
        },
        {
          id: 'scale-samokat',
          chapter: 'ГЛАВА 02 / SCALE',
          company: 'Самокат / ООО «Умное пространство»',
          role: 'Руководитель отдела сопровождения продуктов L3',
          summary:
            'L3-сопровождение платформы Самоката и Мегамаркета: более 250 микросервисов в клиентском и коммерческом блоках.',
          bullets: [
            'Внедрены практики ITIL, SLA, более 20 операционных метрик и элементов мониторинга.',
            'Организован Service Transition для более чем 10 продуктов.',
            'Команда масштабирована в 3 раза; проведено более 150 интервью; 5 специалистов выросли в бизнес-аналитиков.',
          ],
        },
        {
          id: 'operate-postal',
          chapter: 'ГЛАВА 03 / OPERATE',
          company: 'Почтовые технологии',
          role: 'Руководитель отдела информационных технологий',
          summary: 'IT-поддержка 15 высоконагруженных сервисов с общей аудиторией более 800 000 пользователей.',
          bullets: [
            'Incident Management, SLA, межкомандное взаимодействие и управленческая отчётность.',
            'Время решения инцидентов сокращено на 25% за счёт автоматизации и улучшения взаимодействия.',
            'Затраты на техническую поддержку снижены на 20% после регулярной работы с бизнесом по SLA и качеству сервиса.',
          ],
        },
        {
          id: 'incident-rapporto',
          chapter: 'ГЛАВА 04 / INCIDENT / CPaaS',
          company: 'Раппорто',
          role: 'Руководитель клиентской технической поддержки',
          summary:
            'Стабильность клиентского сервиса B2B CPaaS-платформы, SLA и распределённая техническая поддержка.',
          bullets: [
            'Координация L1/L2/L3, разработки, эксплуатации, продукта, аккаунт-менеджеров, операторов связи и внешних партнёров.',
            'REST API, SMPP, SMS, Push, Viber, Telegram; маршрутизация, TTL, статусы доставки, задержки и отказы сервисов.',
            'Аудит OTRS, развитие Service Desk, observability, runbook, Known Issues и эксплуатационной документации.',
          ],
        },
      ],
    },
    pushIncident: {
      eyebrow: 'Разбор реального инцидента',
      title: 'Push-инцидент крупного банка.',
      subtitle: 'От клиентского сигнала о сбое до системных изменений в эксплуатации.',
      badge: 'КЕЙС 02 / INCIDENT MANAGEMENT',
      caseName: 'Push-инцидент крупного банка',
      steps: [
        {
          id: 'signal',
          step: '01',
          phase: 'SIGNAL',
          title: 'Клиент первым сообщил о сбое',
          context: 'Входящий сигнал от банка',
          detail:
            'Клиент (крупный банк) первым сообщил о проблеме: push-уведомления не доходили до конечных пользователей приложения.',
          systemAction:
            'Инцидент зафиксирован со стороны клиента до срабатывания внутренних агрегированных порогов мониторинга.',
        },
        {
          id: 'triage',
          step: '02',
          phase: 'TRIAGE',
          title: 'Расхождение симптомов по платформам',
          context: 'Первичная диагностика L1/L2',
          detail:
            'Диагностика показала разделение проблемы: iOS push не обрабатывался Notification Service, а Android-сценарий зависел от клиентской конфигурации или токенов.',
          systemAction:
            'Локализованы независимые цепочки обработки push-трафика под iOS и Android.',
        },
        {
          id: 'split',
          step: '03',
          phase: 'SPLIT HYPOTHESIS',
          title: 'Разделение платформенной и клиентской причин',
          context: 'Ограничение: отсутствие сквозной трассировки',
          detail:
            'Не было удобной сквозной трассировки и явного ownership. Требовалось разделить платформенную причину и клиентскую конфигурацию без преждевременных выводов.',
          systemAction:
            'Исключены ложные выводы и споры между командами: изолированы проверка ядра отправки CPaaS и аудит токенов на стороне клиента.',
        },
        {
          id: 'ownership',
          step: '04',
          phase: 'OWNERSHIP',
          title: 'Единый ownership и координация команд',
          context: 'Межкомандное взаимодействие',
          detail:
            'Собрал технический контекст, координировал L1/L2/L3, разработку, продукт и аккаунт-менеджеров, организовал прозрачную коммуникацию и регулярный статус для банка.',
          systemAction:
            'Централизовано управление устранением: единый технический координатор связывает инженерный статус с клиентской поддержкой.',
        },
        {
          id: 'followup',
          step: '05',
          phase: 'FOLLOW-UP',
          title: 'Системные изменения и предотвращение',
          context: 'Результат для системы',
          detail:
            'Единичный сбой переведён в системные действия по observability, ownership и документации.',
          systemAction:
            'Сформулированы требования к клиентской наблюдаемости, регламентированы зоны ответственности и обновлены runbooks.',
        },
      ],
      navigation: {
        prev: '← ПРЕДЫДУЩИЙ ЭТАП',
        next: 'СЛЕДУЮЩИЙ ЭТАП →',
        stepOf: (current, total) => `ЭТАП ${current} ИЗ ${total}`,
      },
      sourceNotice: 'Все этапы и технические границы строго соответствуют исходному описанию инцидента.',
    },
    fullExperience: {
      eyebrow: 'Опыт работы',
      title: 'Весь путь, от сигнала до решения.',
      dateDisclaimer: 'Даты не опубликованы в исходном профиле',
      items: [
        {
          id: 'rapporto',
          marker: '01',
          company: 'Раппорто',
          role: 'Руководитель клиентской технической поддержки',
          summary:
            'Стабильность клиентского сервиса B2B CPaaS-платформы, SLA и распределённая техническая поддержка.',
          bullets: [
            'Координация L1/L2/L3, разработки, эксплуатации, продукта, аккаунт-менеджеров, операторов связи и внешних партнёров.',
            'REST API, SMPP, SMS, Push, Viber, Telegram; маршрутизация, TTL, статусы доставки, задержки и отказы сервисов.',
            'Аудит OTRS, развитие Service Desk, observability, runbook, Known Issues и эксплуатационной документации.',
          ],
        },
        {
          id: 'samokat',
          marker: '02',
          company: 'Самокат / ООО «Умное пространство»',
          role: 'Руководитель отдела сопровождения продуктов L3',
          summary:
            'L3-сопровождение платформы Самоката и Мегамаркета: более 250 микросервисов в клиентском и коммерческом блоках.',
          bullets: [
            'Внедрены практики ITIL, SLA, более 20 операционных метрик и элементов мониторинга.',
            'Организован Service Transition для более чем 10 продуктов.',
            'Команда масштабирована в 3 раза; проведено более 150 интервью; 5 специалистов выросли в бизнес-аналитиков.',
          ],
        },
        {
          id: 'postal',
          marker: '03',
          company: 'Почтовые технологии',
          role: 'Руководитель отдела информационных технологий',
          summary:
            'IT-поддержка 15 высоконагруженных сервисов с общей аудиторией более 800 000 пользователей.',
          bullets: [
            'Incident Management, SLA, межкомандное взаимодействие и управленческая отчётность.',
            'Время решения инцидентов сокращено на 25% за счёт автоматизации и улучшения взаимодействия.',
            'Затраты на техническую поддержку снижены на 20% после регулярной работы с бизнесом по SLA и качеству сервиса.',
          ],
        },
        {
          id: 'sber',
          marker: '04',
          company: 'Сбер',
          role: 'Team Lead команды сопровождения',
          summary: 'Сопровождение 50 ключевых сервисов для более чем 2 млн активных пользователей.',
          bullets: [
            'Incident, Problem, Risk и Service Management; координация разработки, DevOps и поддержки.',
            'Внедрена система мониторинга KPI; заявленный SLA — 99,5%.',
            'Время решения критических инцидентов сокращено на 20% в результате оптимизации координации.',
          ],
        },
        {
          id: 'kdl',
          marker: '05',
          company: 'KDL Клинико-диагностические лаборатории',
          role: 'Руководитель отдела технической поддержки',
          summary: 'Построение Service Desk и операционной модели технической поддержки с нуля.',
          bullets: [
            'ITSM-процессы, SLA, найм, развитие команды, автоматизация, бюджет и подрядчики.',
            'Предпроектное исследование, запуск новых направлений, технические задания, регламенты и эксплуатационная документация.',
            'Jira, Confluence и системы мониторинга для задач, знаний и процессов сопровождения.',
          ],
        },
        {
          id: 'energon',
          marker: '06',
          company: 'Энергон',
          role: 'Team Lead Helpdesk',
          summary: 'Построение службы технической поддержки с нуля.',
        },
        {
          id: 'mosenergo',
          marker: '07',
          company: 'МОСЭНЕРГО, ОАО',
          role: 'Team Lead Helpdesk',
          summary: 'Руководство командой из 7 специалистов Helpdesk / Call Center.',
          bullets: [
            'Поддержка более 200 пользователей, эскалации, обучение, отчётность, бюджет и IT-инфраструктура.',
          ],
        },
        {
          id: 'mts',
          marker: '08',
          company: 'МТС',
          role: 'IT Technical Support Specialist L2 / Tier 2',
          summary:
            'Поддержка услуг мобильной связи и мобильного интернета, диагностика, отчётность и обучение.',
        },
        {
          id: 'rosneft',
          marker: '09',
          company: 'Роснефть',
          role: 'Ведущий администратор проектной деятельности / Middle Engineer Help Desk L1',
          summary:
            'IT-проекты, закупки, отчётность, проектная документация, первичная диагностика и эскалация L1/L2/L3.',
        },
      ],
    },
    capabilities: {
      eyebrow: 'Что строю',
      title: 'Поддержка — это система,',
      titleEmphasis: 'а не очередь.',
      items: [
        {
          id: 'cap-1',
          index: '01',
          title: 'Support systems',
          description:
            'L1–L3, единая клиентская история, очереди, SLA/OLA, ownership и понятная эскалация.',
        },
        {
          id: 'cap-2',
          index: '02',
          title: 'Incident operations',
          description:
            'Координация критических инцидентов: impact, таймлайн, коммуникация, RCA и follow-up.',
        },
        {
          id: 'cap-3',
          index: '03',
          title: 'Service transition',
          description:
            'Подготовка новых продуктов к сопровождению: ресурс, документация, метрики и эксплуатационная готовность.',
        },
        {
          id: 'cap-4',
          index: '04',
          title: 'AI-first workflows',
          description:
            'Автоматизация рутины и документация, понятная человеку, системе мониторинга и AI-агенту.',
        },
      ],
    },
    impactSection: {
      eyebrow: 'Масштаб и влияние',
      title: 'Масштаб, который виден.',
      aside:
        'Соединяю клиентский сервис, инженерную диагностику и операционное управление — чтобы проблема доходила до владельца, клиент понимал происходящее, а команда устраняла причину, а не только симптом.',
      items: [
        { stat: '250+', label: 'микросервисов в контуре L3-поддержки' },
        { stat: '15', label: 'высоконагруженных сервисов под управлением' },
        { stat: '10+', label: 'продуктов приняты на сопровождение' },
        { stat: '3×', label: 'масштабирование команды' },
        { stat: '150+', label: 'интервью проведено лично' },
      ],
    },
    casesSection: {
      eyebrow: 'Разбор рабочих ситуаций',
      title: 'От сигнала',
      titleEmphasis: 'к изменению системы.',
      labels: {
        problem: 'Проблема',
        constraint: 'Ограничение',
        action: 'Действие',
        result: 'Результат',
      },
      items: [
        {
          id: 'case-1',
          tag: 'CASE 01 / SERVICE DESIGN',
          title: 'Аудит и развитие Service Desk',
          problem:
            'В OTRS накопились очереди, статусы и ручные маршруты. Часть процесса держалась на договорённостях и личных сообщениях.',
          constraint:
            'Нельзя было переносить хаос в новый красивый интерфейс. Сначала требовалось понять реальные ownership, SLA, роли, каналы и исключения.',
          action:
            'Систематизировал текущий контур, описал очереди и владельцев, сформировал требования к целевой модели, правилам эскалации и единой клиентской истории.',
          result:
            'Появилась основа для сравнения решений и миграции без потери клиентского контекста.',
        },
        {
          id: 'case-2',
          tag: 'CASE 02 / INCIDENT MANAGEMENT',
          title: 'Push-инцидент крупного банка',
          problem:
            'Клиент первым сообщил о сбое: iOS push не обрабатывался Notification Service, Android-сценарий зависел от клиентской конфигурации или токенов.',
          constraint:
            'Не было удобной сквозной трассировки и явного ownership; требовалось разделить платформенную причину и клиентскую конфигурацию без преждевременных выводов.',
          action:
            'Собрал технический контекст, координировал L1/L2/L3, разработку, продукт и аккаунт-менеджеров, организовал коммуникацию и follow-up.',
          result:
            'Единичный сбой переведён в системные действия по observability, ownership и документации.',
        },
        {
          id: 'case-3',
          tag: 'CASE 03 / OBSERVABILITY',
          title: 'Задержки в сценарии логистического клиента',
          problem:
            'Клиент сообщил о замедлении. На отдельных 30-секундных интервалах более 90% запросов обрабатывались дольше 3 секунд.',
          constraint:
            'Клиент в основном использовал нотификации, а API опрашивал только при пропущенных статусах. Среднее время ответа API не описывало реальный impact.',
          action:
            'Сопоставил клиентский сценарий с таймингами и метриками, подготовил обращение к разработке и эксплуатации, сформулировал требования к отдельной клиентской наблюдаемости.',
          result:
            'Фокус расследования сместился с одной медленной операции на массовую деградацию интервала и клиентский путь.',
        },
      ],
    },
    principles: {
      eyebrow: 'Как работаю',
      title: 'Меньше шума.',
      titleEmphasis: 'Больше сигнала.',
      aside:
        'Рассматриваю поддержку как функцию, которая одновременно обеспечивает стабильность сервиса и помогает продуктовым и инженерным командам находить системные проблемы.',
      items: [
        {
          id: 'remove',
          index: '01',
          keyword: 'REMOVE',
          title: 'Убирать лишние переходы',
          description:
            'Единая клиентская история важнее внутренней структуры отделов. Клиент не должен пересказывать проблему нескольким командам.',
        },
        {
          id: 'simplify',
          index: '02',
          keyword: 'SIMPLIFY',
          title: 'Упрощать ownership',
          description:
            'У каждого инцидента должен быть понятный владелец коммуникации, диагностики и исправления.',
        },
        {
          id: 'measure',
          index: '03',
          keyword: 'MEASURE',
          title: 'Измерять impact',
          description:
            'Зелёный дашборд не равен здоровому клиентскому пути. Смотрю на latency, очереди, статусы, повторы и деградацию сценария.',
        },
        {
          id: 'automate',
          index: '04',
          keyword: 'AUTOMATE',
          title: 'Автоматизировать повторяемое',
          description:
            'Рутину превращаю в метрику, runbook, автоматизацию или понятную задачу для продукта и инженерии.',
        },
      ],
    },
    toolStack: {
      eyebrow: 'Инструменты',
      title: 'Рабочий',
      titleEmphasis: 'слой.',
      groups: [
        {
          id: 'service-mgmt',
          title: 'Service management',
          chips: ['OTRS', 'Jira', 'Yandex Tracker', 'ITIL / ITSM', 'SLA / OLA'],
        },
        {
          id: 'monitoring-logs',
          title: 'Monitoring / logs',
          chips: ['Grafana', 'Grafana OnCall', 'Zabbix', 'Loki', 'ELK', 'Graylog'],
        },
        {
          id: 'integration',
          title: 'Integration domain',
          chips: ['REST API', 'SMPP', 'SMS', 'Push', 'Viber', 'Telegram', 'Webhooks', 'TTL'],
        },
        {
          id: 'working-layer',
          title: 'Working layer',
          chips: ['Linux', 'Docker', 'SQL', 'Git', 'Runbooks', 'Knowledge Management', 'AI tools'],
        },
      ],
    },
    aboutContext: {
      eyebrow: 'Контекст',
      title: 'Руководитель поддержки',
      titleEmphasis: 'с технической глубиной.',
      paragraphs: [
        'Руководитель технической поддержки и сервисных операций с более чем 13-летним опытом в финтехе, e-commerce, телекоме и enterprise.',
        'Специализируюсь на построении служб технической поддержки, Incident & Problem Management, запуске новых продуктов на сопровождение, observability и развитии сервисных процессов.',
        'Рассматриваю поддержку как функцию, которая одновременно обеспечивает стабильность сервиса и помогает продуктовым и инженерным командам находить системные проблемы.',
      ],
      tags: [
        'Customer Support',
        'Service Operations',
        'Incident Management',
        'Customer Success',
        'Support Engineering',
        'AI-first workflows',
      ],
    },
    contact: {
      eyebrow: 'Контакт',
      title: 'Есть сложный support-контур?',
      description:
        'Обсудим, где теряется клиентский сигнал, как вернуть ownership и какие изменения дадут команде больше управляемости.',
      email: 'dmitry@yakovlefff.ru',
    },
    footer: {
      brandTag: 'Дмитрий Яковлев / Support Systems',
      backToTop: 'На главную ↗',
    },
    modeBanner: {
      title30s: 'РЕЖИМ 30 СЕКУНД',
      desc30s: 'Быстрый обзор: топология сигнала, подтверждённый масштаб, ключевой опыт и разбор инцидента с push-уведомлениями.',
      switchPrompt: 'Хотите изучить полную хронологию опыта, принципы работы и рабочий стек инструментов?',
      switchToFull: 'Перейти к полной версии CV (9 компаний и стек) →',
      switchTo30s: '← Вернуться к экспресс-режиму 30 сек',
    },
  },
};
